// Multipliers to milliseconds for the time units the backends emit as measure units. Shared by the
// aggregate table and the chart so both scale the magnitude rather than abbreviating the base unit
// (a chart axis in "billions of ns").
const MS_PER_TIME_UNIT: Record<string, number> = {
  ns: 1e-6,
  us: 1e-3,
  µs: 1e-3,
  ms: 1,
  s: 1000,
};

export const isTimeUnit = (unit: string | undefined): boolean =>
  unit != null && unit in MS_PER_TIME_UNIT;

/** A duration split into magnitude and unit, for callers that style the two differently. */
export interface ScaledDuration {
  value: string;
  unit: string;
}

// Two decimals, trailing zeros trimmed. Rounding to the whole unit collapsed distinct values onto one
// label: a table of p50 span durations read "1 µs" per row while the bars visibly differed.
const scaled = (magnitude: number, unit: string): ScaledDuration => ({
  value: magnitude.toFixed(2).replace(/\.?0+$/, ""),
  unit,
});

// The unit a magnitude reads best in. Shared, so the abbreviated and exact forms cannot disagree.
const durationUnitFor = (ms: number): string => {
  const magnitude = Math.abs(ms);
  if (magnitude >= 1000) return "s";
  if (magnitude >= 1) return "ms";
  if (magnitude >= 1e-3) return "µs";
  return "ns";
};

// Scale a value in `unit` to a human magnitude: 69280 ms → 69.28 s; 1400 ns → 1.4 µs.
export const scaleDurationValue = (value: number, unit: string): ScaledDuration => {
  const ms = value * (MS_PER_TIME_UNIT[unit] ?? 1);
  // Zero has no magnitude to scale by, and falling through would floor it to the smallest unit: an
  // axis of millisecond ticks would label its origin "0 ns". Keep the series' own unit instead.
  if (ms === 0) return { value: "0", unit };
  const target = durationUnitFor(ms);
  // The floor; a fraction of a ns is noise, and two decimals would print it.
  if (target === "ns") return { value: String(Math.round(ms * 1e6)), unit: "ns" };
  return scaled(ms / MS_PER_TIME_UNIT[target], target);
};

/**
 * Every digit the value carries, in the unit the abbreviated form quotes: 156364275712 ns →
 * "156.364275712 s". For copying a number out, where two decimals lose most of it.
 */
export const exactDurationValue = (value: number, unit: string): ScaledDuration => {
  const ms = value * (MS_PER_TIME_UNIT[unit] ?? 1);
  if (ms === 0) return { value: "0", unit };
  const target = durationUnitFor(ms);
  // The unit change is a float multiply, so it can leave 156.36427571200001 behind. Fifteen
  // significant digits drops that tail without reaching a digit a double actually holds.
  const magnitude = Number((ms / MS_PER_TIME_UNIT[target]).toPrecision(15));
  return { value: String(magnitude), unit: target };
};

export const formatDurationValue = (value: number, unit: string): string => {
  const duration = scaleDurationValue(value, unit);
  return `${duration.value} ${duration.unit}`;
};
